import { useState } from 'react';
import type { ReactNode } from 'react';
import { SaveHint, useAutoSave, useDebouncedAutoSave } from '../../components/ui';

export function KeyValueRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="kv-row">
      <div className="kv-label">
        <span>{label}</span>
        {hint}
      </div>
      <div className="kv-value">{children}</div>
    </div>
  );
}

/**
 * A text value that saves itself.
 *
 * `debounce` is for long text: it saves quietly while somebody types rather
 * than waiting for a blur, because a paragraph is written in pauses and a
 * drawer can be closed without the field ever blurring. Both variants flush on
 * blur, so nothing queued is lost either way — see autosave.tsx, where that
 * was a real defect until 8h.
 *
 * Before this prop, the one multiline field in the lead drawer was a raw
 * <textarea> wired to its own debounced saver beside this component, because
 * this component could not debounce. Two implementations of one idea, and the
 * raw one is what "too many different-looking controls" was actually made of.
 */
export function KeyValueText({
  label,
  value,
  placeholder,
  multiline = false,
  debounce = false,
  onCommit,
}: {
  label: string;
  value: string;
  placeholder?: string;
  multiline?: boolean;
  /** Save while typing rather than on blur. For long text. */
  debounce?: boolean;
  onCommit: (next: string) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState(value);
  // Both hooks run, and one is chosen. A ternary over the two would be a
  // conditional hook: `debounce` is fixed per call site today, so it would
  // work, and it would break the day a field decides its own mode from data.
  // Both are a little state and a couple of refs; the cost of holding the
  // unused one is nothing next to a rule somebody has to suppress to read.
  const immediate = useAutoSave<string>(onCommit);
  const debounced = useDebouncedAutoSave<string>(onCommit);
  const save = debounce ? debounced : immediate;

  return (
    <KeyValueRow label={label} hint={<SaveHint state={save.state} error={save.error} />}>
      {multiline ? (
        <textarea
          className="kv-input kv-input--multiline"
          rows={3}
          value={draft}
          placeholder={placeholder}
          aria-label={label}
          onChange={(event) => {
            setDraft(event.target.value);
            if (debounce) {
              save.save(event.target.value);
            }
          }}
          onBlur={() => {
            if (debounce) {
              save.flush();
            } else {
              save.save(draft);
            }
          }}
        />
      ) : (
        <input
          className="kv-input"
          type="text"
          value={draft}
          placeholder={placeholder}
          aria-label={label}
          onChange={(event) => {
            setDraft(event.target.value);
            if (debounce) {
              save.save(event.target.value);
            }
          }}
          onBlur={() => {
            if (debounce) {
              save.flush();
            } else {
              save.save(draft);
            }
          }}
        />
      )}
    </KeyValueRow>
  );
}

/**
 * A date that saves itself as soon as it is picked.
 *
 * A date is a selection, like a select, so it saves on change rather than on
 * blur: a person can pick a date and close the drawer with Escape while the
 * field still has focus, and a blur-only save would lose it without a word.
 * Only a complete date (or a cleared field) is written, so typing a year one
 * digit at a time (0002, 0020, 0202, 2026) never saves the pieces.
 */
export function KeyValueDate({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: string | null;
  onCommit: (next: string | null) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState(value ?? '');
  const save = useAutoSave<string>((next) => onCommit(next === '' ? null : next));

  return (
    <KeyValueRow label={label} hint={<SaveHint state={save.state} error={save.error} />}>
      <input
        className="kv-input"
        type="date"
        value={draft}
        aria-label={label}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          if (next === '' || /^[12]\d{3}-\d{2}-\d{2}$/.test(next)) {
            save.save(next);
          }
        }}
      />
    </KeyValueRow>
  );
}

/** The company drawer's two rooms. */
